'use client';

import { c as _c } from "react/compiler-runtime";
import { type ToggleDialog, useDialogContext } from '@morpho-org/ui-v6/contexts';
import { type ComponentType, type ReactNode, useRef, useState } from 'react';
import type { ActionFlowExit } from '@/components/ActionFlow/ActionFlowReviewDialog';
import type { TxSourceFlow } from '@/lib/shared/utils/flags.constants';
import { ErrorServiceUnavailable } from '@/components/common/Error';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { VaultsActionFlowProvider } from '@/components/providers/VaultsActionFlowProvider';
import { useIsActionFlowEnabled } from '@/lib/shared/components/FeatureFlagsProvider';
type ActionFlowInput<Input extends object> = Input & {
  onExit: (exit: ActionFlowExit) => void;
};
const ACTION_FLOW_ERROR_FALLBACK = <ErrorServiceUnavailable minHeight="494px" />;
export interface ActionFlowReviewDialogs<Input extends object> {
  ActionFlowDialog: ComponentType<ActionFlowInput<Input>>;
  renderLegacy: (input: Input) => ReactNode;
}
interface ActionFlowReviewOptions {
  dialogOptions?: Parameters<ToggleDialog>[1];
  onCompletedExit?: () => void;
}
export function useActionFlowReview(flow, dialogs, t0) {
  const $ = _c(17);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    isOpen: isParentDialogOpen,
    setDialogOptions,
    toggleDialog
  } = useDialogContext();
  const isActionFlowEnabled = useIsActionFlowEnabled(flow);
  const {
    ActionFlowDialog,
    renderLegacy
  } = dialogs;
  const [actionFlowReview, setActionFlowReview] = useState(null);
  const savedDialogClosable = useRef(false);
  const {
    dialogOptions,
    onCompletedExit
  } = options;
  let t2;
  if ($[2] !== dialogOptions || $[3] !== isActionFlowEnabled || $[4] !== isParentDialogOpen || $[5] !== onCompletedExit || $[6] !== renderLegacy || $[7] !== setDialogOptions || $[8] !== toggleDialog) {
    t2 = input => {
      if (isActionFlowEnabled) {
        if (isParentDialogOpen) {
          savedDialogClosable.current = true;
          setDialogOptions(_temp);
        }
        setActionFlowReview({
          ...input,
          onExit: t3 => {
            const {
              completed
            } = t3;
            setActionFlowReview(null);
            if (savedDialogClosable.current) {
              savedDialogClosable.current = false;
              setDialogOptions(prev_0 => ({
                ...prev_0,
                closable: dialogOptions?.closable
              }));
            }
            if (completed) {
              onCompletedExit?.();
            }
          }
        });
        return;
      }
      toggleDialog(<SectionErrorBoundary fallback={ACTION_FLOW_ERROR_FALLBACK}>{renderLegacy(input)}</SectionErrorBoundary>, dialogOptions);
    };
    $[2] = dialogOptions;
    $[3] = isActionFlowEnabled;
    $[4] = isParentDialogOpen;
    $[5] = onCompletedExit;
    $[6] = renderLegacy;
    $[7] = setDialogOptions;
    $[8] = toggleDialog;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const open = t2;
  let t3;
  if ($[10] !== ActionFlowDialog || $[11] !== actionFlowReview) {
    t3 = actionFlowReview ? <VaultsActionFlowProvider><SectionErrorBoundary fallback={ACTION_FLOW_ERROR_FALLBACK}><ActionFlowDialog {...actionFlowReview} /></SectionErrorBoundary></VaultsActionFlowProvider> : null;
    $[10] = ActionFlowDialog;
    $[11] = actionFlowReview;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const element = t3;
  const t4 = actionFlowReview !== null;
  let t5;
  if ($[13] !== element || $[14] !== open || $[15] !== t4) {
    t5 = {
      open,
      element,
      isOpen: t4
    };
    $[13] = element;
    $[14] = open;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp(prev) {
  return {
    ...prev,
    closable: false
  };
}